import {DataStatus, Switch, Choice, Card, Separator, Tooltip} from 'platform/components';
import {Box, HStack, Heading, Show, Text, VStack} from 'platform/foundation';

import {useId, useState} from 'react';

import {isNotNil} from 'ramda';

import {
  useGetServiceOrderVehicleClassQuery,
  useGetVehicleClassesQuery,
  usePostServiceOrderAssignVehicleClassMutation,
  usePostServiceOrderUnassignVehicleClassMutation,
} from '@dms/api/workshop';
import i18n from '@dms/i18n';
import {handleApiError} from '@dms/shared';

import {ApiError, TestIdProps, suffixTestId} from 'shared/web';

import {PRICING_FIELD_WIDTH} from '../constants/PRICING_FIELD_WIDTH';

interface VehicleClassSectionProps extends TestIdProps {
  serviceCaseId: string;
  serviceOrderId: string;
  isReadOnly?: boolean;
  canEditVehicleClass: boolean;
}

export function VehicleClassSection(props: VehicleClassSectionProps) {
  const [selectedVehicleClassId, setSelectedVehicleClassId] = useState<string>();
  const [isAssignEnabled, setIsAssignEnabled] = useState(false);
  const assignSwitchId = useId();

  const {data, isLoading, isError} = useGetServiceOrderVehicleClassQuery({
    serviceCaseId: props.serviceCaseId,
    serviceOrderId: props.serviceOrderId,
  });

  const {
    data: vehicleClasses,
    isLoading: isVehicleClassesLoading,
    isError: isVehicleClassesError,
  } = useGetVehicleClassesQuery();

  const [assignVehicleClass, {isLoading: isAssigning}] =
    usePostServiceOrderAssignVehicleClassMutation();
  const [unassignVehicleClass, {isLoading: isUnassigning}] =
    usePostServiceOrderUnassignVehicleClassMutation();

  const handleToggleAssign = (enabled: boolean) => {
    setIsAssignEnabled(enabled);
    if (!enabled) {
      unassignVehicleClass({
        serviceCaseId: props.serviceCaseId,
        serviceOrderId: props.serviceOrderId,
      })
        .unwrap()
        .then(() => {
          setSelectedVehicleClassId(undefined);
        })
        .catch((e: ApiError) => {
          handleApiError(e);

          setIsAssignEnabled(true);
        });
    }
  };

  const handleVehicleClassChange = (vehicleClassId?: string | null) => {
    setSelectedVehicleClassId(vehicleClassId ?? undefined);
    if ((isAssignEnabled || isNotNil(data?.vehicleClassId)) && vehicleClassId) {
      assignVehicleClass({
        serviceCaseId: props.serviceCaseId,
        serviceOrderId: props.serviceOrderId,
        vehicleClassId,
      })
        .unwrap()
        .catch((e: ApiError) => {
          handleApiError(e);

          setSelectedVehicleClassId(data?.vehicleClassId);
        });
    }
  };

  const currentVehicleClassId = data?.vehicleClassId ?? selectedVehicleClassId;
  const isDisabled = props.isReadOnly || !props.canEditVehicleClass || isAssigning || isUnassigning;

  const vehicleClassOptions =
    vehicleClasses
      ?.filter((vehicleClass) => vehicleClass.isActive)
      .map((vehicleClass) => ({
        value: vehicleClass.vehicleClassId,
        label: vehicleClass.name,
      })) ?? [];

  return (
    <DataStatus
      isLoading={isLoading || isVehicleClassesLoading}
      isError={isError || isVehicleClassesError}
      minHeight={24}
    >
      <Card variant="inlineGrey">
        <VStack spacing={4}>
          <VStack spacing={1}>
            <Heading size={4}>{i18n.t('entity.vehicleClass.labels.vehicleClass')}</Heading>
            <Text size="small" color="secondary">
              {i18n.t('entity.order.labels.vehicleClassDescription')}
            </Text>
          </VStack>
          <Separator spacing={0} />
          <HStack align="center" spacing={3}>
            <Tooltip
              isDisabled={props.canEditVehicleClass}
              label={i18n.t('general.labels.noPermission')}
            >
              <Switch
                externalLabelId={assignSwitchId}
                value={isNotNil(data?.vehicleClassId) || isAssignEnabled}
                onChange={handleToggleAssign}
                isDisabled={isDisabled}
                data-testid={suffixTestId('assignVehicleClassSwitch', props)}
              />
            </Tooltip>
            <label htmlFor={assignSwitchId}>
              <Heading size={4}>{i18n.t('entity.order.actions.assignVehicleClass')}</Heading>
            </label>
          </HStack>
          <Show when={isNotNil(data?.vehicleClassId) || isAssignEnabled}>
            <Box width={PRICING_FIELD_WIDTH} maxWidth="100%">
              <Tooltip
                isDisabled={props.canEditVehicleClass}
                label={i18n.t('general.labels.noPermission')}
              >
                <Choice
                  label={i18n.t('entity.vehicleClass.labels.vehicleClass')}
                  value={currentVehicleClassId ?? null}
                  onChange={handleVehicleClassChange}
                  options={vehicleClassOptions}
                  isDisabled={isDisabled}
                  isLoading={isAssigning}
                  isNotClearable
                  data-testid={suffixTestId('vehicleClassSelect', props)}
                />
              </Tooltip>
            </Box>
          </Show>
        </VStack>
      </Card>
    </DataStatus>
  );
}
